import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { CmsCore, CmsLineDataPlanCollection, CmsMyAccount, CmsPlanSwitch } from 'store/cms/models';
import { AccountSummary, LineSummary, PlanSummary, SummaryLineWithPlan } from 'store/account-summary/models';
import { CimaCore, Cloudinary, Formatter, Util, XmStore, XmStoreUtil } from 'core/services';
import { CONFIG_TOKEN, PlanGroupType, unlPlanNames } from 'core/constants';
import { DiscountPromoCollection } from 'store/promotions/discount-promotion/models';

import { PlanEligibility } from 'store/line/models';
import { LineDataPlan } from 'store/line/models/line-data-plan';
import { User } from 'store/user/models';
import { IXMOptions } from 'core/interfaces';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { planEligibilityDetails } from 'store/plan-eligibility/models';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';

@Component({
    selector: 'xm-scheduled-in-progress',
    templateUrl: './scheduled-in-progress.html',
    styleUrls: ['./scheduled-in-progress.scss'],
    standalone: false
})
export class PlanSwitchScheduledInProgressComponent implements OnInit, OnDestroy {
    public pageData: CmsPlanSwitchData;
    public selectedPlanData: CmsDataPlanDetails;
    public isUnlimited: boolean;
    public iconOption: MediaImageOptions;
    public subhead: string;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public inusePlan: LineDataPlan;
    public inusePlanCode: string;
    public inusePlanName: string;
    public inusePlanData: CmsDataPlanDetails;
    public checkmarkIcon: MediaImageOptions;
    public accountSummary: AccountSummary;
    public currentPlan: PlanSummary;
    public lineSummary: LineSummary;
    public currentLine: LineSummary;
    public byTheGigPlanName: string;
    public unlimitedPlanName: string;
    public pageHeader: string;
    public user: User;
    public isCbmUserSegmentFiveTabletUnlimited: boolean = false;
    public isBusinessUser: boolean;
    public isSelectedPlanBtg: boolean = false;
    public isCBMSegmentFive: boolean = false;
    public planSevenPlanEligibility: targetPlanSwitchDetails;
    public eligiblePlanSevenDetails: PlanCardsDetails;
    public planSevenInUsePlan: PlanCardsDetails;
    public isPlanSevenPlanSwitchResi: boolean;
    public isPlanSevenPlanSwitchCBM: boolean;
    public isSeg6ResiUser: boolean;
    public pricePerUnit: string;
    public planName: string;
    public planDetails: planEligibilityDetails;
    public isCbmGemini: boolean = false;
    public isDiscountPromotion: boolean = false;
    public promoResponse: DiscountPromoCollection;

    private xmStore: XmStore;
    private state: StateService;
    private subscriptions: Subscription[] = [];
    private planCode: string;
    private scheduledDate: string;
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private xmcPlanSwitchService: XmcPlanSwitchService;

    constructor(state: StateService, xmStore: XmStore, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions,  xmcPlanSwitchService: XmcPlanSwitchService) {
        Object.assign(this, { state, xmStore, cimaCore, config, xmcPlanSwitchService });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.scheduledDate = this.state.params.scheduled_date;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.pageData = result.scheduledSwitchInProgress;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<DiscountPromoCollection>(DiscountPromoCollection), (result: DiscountPromoCollection) => {
            this.promoResponse = result;
            if (this.promoResponse) {
                this.isDiscountPromotion = this.promoResponse && (this.promoResponse.isLinePromoEligible || this.promoResponse.isAccountPromoEligible 
                    || this.promoResponse.isTradeInDDAPromoEligible || this.promoResponse.isExtendedPromoDollarEligible);
            }
        }));
        
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccount>(CmsMyAccount), (cmsMyAccount: CmsMyAccount) => {
            this.byTheGigPlanName = cmsMyAccount.plans.byTheGig;
            this.unlimitedPlanName = cmsMyAccount.plans.unlimited;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.checkmarkIcon = {
                mobile: Cloudinary.generateMediaFromCms(result.icons.greenCircleCheck)
            };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            if (this.accountSummary) {
                const lineByKey: SummaryLineWithPlan = this.accountSummary.getLineSummaryWithPlanByKey(this.state.params.line_id);
                this.currentPlan = lineByKey.plan;
                this.lineSummary = accountSummary.getLineByKey(this.state.params.line_id);
                this.scheduledDate = this.scheduledDate? this.scheduledDate : this.lineSummary?.scheduledSwitch?.scheduleDate;
            }
        }));

        this.planCode = decodeURIComponent(this.state.params.plan_code);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.selectedPlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.planCode));
            this.iconOption = {
                mobile: Cloudinary.generateMediaFromCms(this.selectedPlanData.miniIcon)
            };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.isPlanSevenPlanSwitchResi = !this.isBusinessUser && this.user.account.isSegmentFive;
            this.isPlanSevenPlanSwitchCBM = this.isBusinessUser && this.user.account.isSegmentFive;
            this.isSeg6ResiUser = !this.isBusinessUser && this.user.account.isSegmentSix;
            this.isCbmGemini = this.config.ENABLE_CBM_GEMINI && this.user.account.isNoLevel && this.isBusinessUser;

            this.isCbmUserSegmentFiveTabletUnlimited = this.isBusinessUser && this.user.account.isSegmentFive && (this.lineSummary.isUnlBasic || this.lineSummary.isUnlPremium) && this.lineSummary.isTablet;
            this.isSelectedPlanBtg = (this.selectedPlanData.planType === PlanGroupType.BY_THE_GIG_SHARED);
            this.isCBMSegmentFive = this.isBusinessUser && this.user.account.isSegmentFive;
        }));

        if (this.isPlanSevenPlanSwitchResi || this.isPlanSevenPlanSwitchCBM || this.isSeg6ResiUser || this.isCbmGemini) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<planEligibilityDetails>(planEligibilityDetails), (result: planEligibilityDetails) => {
                this.planDetails = result;
                this.inusePlanName = Util.createPascalCase(this.planDetails.currentPlan.planName);
            }));

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (result: targetPlanSwitchDetails) => {
                this.planSevenPlanEligibility = result;
                this.eligiblePlanSevenDetails = this.planSevenPlanEligibility.selectionPage.planCards.find((plan: PlanCardsDetails) => plan.planCode === this.planCode);
                this.isUnlimited = unlPlanNames.includes(this.eligiblePlanSevenDetails.name.toUpperCase());
                this.pageHeader = this.eligiblePlanSevenDetails.name;
                this.pricePerUnit = this.eligiblePlanSevenDetails.price;
                if (this.isCbmGemini) {
                    this.subhead = this.pageData.cbmGeminiProToStandardSubhead;
                } else if (this.isSeg6ResiUser) {
                    const selectedLineIdKeys: string[] = this.xmcPlanSwitchService.getSelectedLineIdKeys();
                    this.subhead = selectedLineIdKeys.length > 0 ? this.pageData.plan8XMCSubhead : this.pageData.plan8NoXMCSubhead;
                } else {
                    this.subhead = this.isBusinessUser && !this.isCbmGemini ? this.pageData.cbmBtgSubhead: this.pageData.subhead;
                }
                this.planName = this.eligiblePlanSevenDetails.name;
                this.buildSubhead(this.subhead);    
            }));
        } else {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
                filters: { lineKey: this.state.params.line_id }
            }), (planEligibility: PlanEligibility) => {
                this.planEligibility = planEligibility;
                this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === this.planCode);
                this.isUnlimited = this.eligiblePlanDetails.isUnlimited;
                if (this.isUnlimited) {
                    this.pageHeader = this.selectedPlanData.planType;
                    this.pageHeader = this.capitalizeWord(this.pageHeader);
                } else {
                    this.pageHeader = this.selectedPlanData.planName;
                }
                this.pricePerUnit = this.isUnlimited ? this.eligiblePlanDetails.postSwitch.lineCost.toString() : this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
                this.inusePlanName = this.getinusePlanName();
                this.subhead = this.isSelectedPlanBtg||this.currentPlan.isTablet? this.pageData.cbmBtgSubhead: this.pageData.cbmSubhead;
                this.planName = this.isCbmUserSegmentFiveTabletUnlimited ? this.selectedPlanData.tabletPlanName : this.selectedPlanData.planName;
                this.buildSubhead(this.subhead);    
            }));
        }
    }

    public ngOnDestroy(): void {
        this.xmcPlanSwitchService.clearPlanSwitchState();
        Util.unsubscribeAll(this.subscriptions);
    }

    public continue(): void {
        this.state.go('account.activity.details', { line_id: this.state.params.line_id });
    }

    private buildSubhead(subhead: string): void {
        if (this.isPlanSevenPlanSwitchResi || this.isPlanSevenPlanSwitchCBM || this.isSeg6ResiUser || this.isCbmGemini) {
            this.subhead = subhead.replace(/\[NEW_PLAN_NAME\]/g, this.planName);
        } else {
            if (this.isBusinessUser) {
                this.subhead = subhead.replace(/\[NEW_PLAN_NAME\]/g, this.selectedPlanData.cbmPlanName);
            } else {
                this.subhead = subhead.replace(/\[NEW_PLAN_NAME\]/g, this.selectedPlanData.planName);
            }
        }
        this.subhead = this.subhead.replace(/\[CURRENT_PLAN_NAME\]/g, this.inusePlanName);
        this.subhead = this.subhead.replace(/\[DATE\]/g, Formatter.formatMonthDateYear(this.scheduledDate));
    }

    private getinusePlanName(): string {
        if (this.isUnlimited && this.currentPlan.isUnlimited) {
            this.inusePlan = this.planEligibility.plans.find((plan: LineDataPlan) => plan.isIneligibleInUse);
            this.inusePlanCode = this.inusePlan.planCode;

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
                this.inusePlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.inusePlanCode));
            }));
            
            return this.isBusinessUser ? this.inusePlanData.cbmPlanName : this.inusePlanData.planName;
        }
        if (this.isUnlimited && this.currentPlan.isTablet) {
            this.inusePlan = this.planEligibility.plans.find((plan: LineDataPlan) => plan.isIneligibleInUse);
            this.inusePlanCode = this.inusePlan.planCode;

       
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
                this.inusePlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.inusePlanCode));
            }));
            
            return this.inusePlanData.tabletPlanName;
        }
        if (!this.isUnlimited) {
            this.currentLine = this.accountSummary.getLineByKey(this.state.params.line_id);
            
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
                this.inusePlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.currentLine.planCode));
            }));
            
            return this.inusePlanData.planName;
        } else {
            
            return this.byTheGigPlanName;
        }
        
    }

    private capitalizeWord(value: string): string {
        return Util.createPascalCase(value);
    }
}
